import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const DESTRUCTIVE_COPY =
  "Această acțiune este ireversibilă. Vor fi șterse toate evaluările, rapoartele generate și istoricul.";

type DeleteCompanyDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyName: string;
  isDeleting: boolean;
  onConfirm: () => Promise<void> | void;
};

export function DeleteCompanyDialog({
  open,
  onOpenChange,
  companyName,
  isDeleting,
  onConfirm,
}: DeleteCompanyDialogProps) {
  const [typedName, setTypedName] = useState("");

  useEffect(() => {
    if (!open) setTypedName("");
  }, [open]);

  const expected = companyName.trim();
  const isMatch = typedName.trim() === expected && expected.length > 0;

  const handleConfirm = async () => {
    if (!isMatch || isDeleting) return;
    await onConfirm();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="portfolio-delete-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>Șterge compania</DialogTitle>
          <DialogDescription
            data-testid="portfolio-delete-dialog-warning"
            className="text-ds-text-600"
          >
            {DESTRUCTIVE_COPY}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <p className="text-sm text-ds-text-600">
            Pentru confirmare, scrie exact numele companiei:
          </p>
          <p
            data-testid="portfolio-delete-dialog-name"
            className="font-mono text-sm font-semibold text-navy-900"
          >
            {expected}
          </p>
          <Label htmlFor="portfolio-delete-confirm-input" className="sr-only">
            Confirmă numele companiei
          </Label>
          <Input
            id="portfolio-delete-confirm-input"
            data-testid="portfolio-delete-dialog-input"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            placeholder={expected}
            autoComplete="off"
            disabled={isDeleting}
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
            data-testid="portfolio-delete-dialog-cancel"
          >
            Anulează
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!isMatch || isDeleting}
            data-testid="portfolio-delete-dialog-confirm"
          >
            {isDeleting ? "Se șterge..." : "Șterge definitiv"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
